// src/shared/ui/Button.jsx
import React from 'react';
import styles from './Button.module.css';

/**
 * Componente Button unificado del Design System Citamed.
 *
 * @param {'primary'|'success'|'warning'|'destructive'|'ghost'|'outline'} variant
 * @param {'sm'|'md'|'lg'} size
 * @param {boolean} iconOnly - Si es true, el botón solo muestra el ícono (cuadrado/circular)
 * @param {React.ReactNode} icon - Componente de ícono (ej: <SaveIcon />)
 * @param {'left'|'right'} iconPosition - Posición del ícono respecto al texto
 * @param {boolean} loading
 * @param {boolean} disabled
 */
export default function Button({
  children,
  variant = 'primary',
  size = 'md',
  iconOnly = false,
  icon,
  iconPosition = 'left',
  loading = false,
  disabled = false,
  onClick,
  type = 'button',
  className = '',
  ...rest
}) {
  const isDisabled = disabled || loading;

  const classNames = [
    styles.button,
    styles[variant],
    iconOnly ? styles.iconOnly : styles[size],
    loading ? styles.loading : '',
    isDisabled ? styles.disabled : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type={type}
      className={classNames}
      onClick={onClick}
      disabled={isDisabled}
      aria-busy={loading}
      aria-label={iconOnly && !loading ? (typeof children === 'string' ? children : 'Botón de acción') : undefined}
      {...rest}
    >
      {/* Spinner de carga */}
      {loading && (
        <span className={styles.spinner} aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
            <path
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              fill="currentColor"
              opacity="0.75"
            />
          </svg>
        </span>
      )}

      {/* Ícono a la izquierda (solo si no está cargando) */}
      {!loading && icon && iconPosition === 'left' && !iconOnly && (
        <span className={styles.icon}>{icon}</span>
      )}

      {/* Texto del botón (oculto si es iconOnly) */}
      {!iconOnly && <span className={styles.label}>{children}</span>}

      {/* Ícono a la derecha (solo si no está cargando) */}
      {!loading && icon && iconPosition === 'right' && !iconOnly && (
        <span className={styles.icon}>{icon}</span>
      )}

      {/* Ícono único (para botones de acción en tablas, etc.) */}
      {iconOnly && icon && !loading && <span className={styles.iconOnlyContent}>{icon}</span>}
    </button>
  );
}